import type React from 'react';

import { Skeleton } from '~/components/ui/skeleton';

type PageSkeletonVariant = 'page' | 'table' | 'form' | 'dashboard';

interface PageSkeletonProps {
  variant?: PageSkeletonVariant;
}

function PageSkeletonPage() {
  return (
    <div className="space-y-6 p-6">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-96" />
      <div className="space-y-4 pt-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

function PageSkeletonTable() {
  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-32" />
      </div>
      <Skeleton className="h-10 w-full" />
      {Array.from({ length: 5 }, (_, i) => (
        <Skeleton key={i} className="h-14 w-full" />
      ))}
    </div>
  );
}

function PageSkeletonForm() {
  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <Skeleton className="h-8 w-48" />
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      ))}
      <Skeleton className="h-10 w-32" />
    </div>
  );
}

function PageSkeletonDashboard() {
  return (
    <div className="space-y-6 p-6">
      <Skeleton className="h-8 w-48" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-28 w-full rounded-lg" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Skeleton className="h-64 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    </div>
  );
}

const variantComponents: Record<PageSkeletonVariant, () => React.JSX.Element> = {
  page: PageSkeletonPage,
  table: PageSkeletonTable,
  form: PageSkeletonForm,
  dashboard: PageSkeletonDashboard,
};

/**
 * Full-page loading skeleton with multiple layout variants.
 * Used as the Suspense fallback for lazy-loaded routes.
 */
export function PageSkeleton({ variant = 'page' }: PageSkeletonProps) {
  const VariantComponent = variantComponents[variant];
  return <VariantComponent />;
}
